/**
 * Le micro, version enregistreur.
 *
 * On capte le son pendant que le bouton est tenu, et on l'envoie a
 * `/api/transcrire` au relachement. La transcription se fait sur le serveur —
 * ce qui change tout par rapport a `webkitSpeechRecognition` :
 *
 *  - **la qualite ne depend plus de l'appareil.** Le moteur d'Apple etait
 *    imposé, avec son modele et ses caprices ; le meme son donne desormais le
 *    meme texte sur un iPhone, un Android ou un portable.
 *  - **plus de coupure en plein milieu.** Il n'y a plus de moteur a
 *    redemarrer : on enregistre, point.
 *  - **le francais du Quebec est annonce** au modele (`fr-CA`), la ou le
 *    moteur embarque faisait ce qu'il voulait.
 *
 * Ce fichier ne fait que capturer. Il ne decide rien : c'est l'appui sur le
 * bouton qui ouvre et ferme le tour.
 */

/**
 * Formats par ordre de preference.
 *
 * Safari n'enregistre pas en WebM — il ne connait que MP4. Chrome et Firefox
 * preferent Opus. On demande donc au navigateur ce qu'il sait faire plutot que
 * d'imposer un format, sans quoi l'enregistrement echoue sur iPhone, c'est-a-
 * dire exactement la ou l'on en a besoin.
 */
const FORMATS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export function enregistrementPossible(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined
  );
}

function formatRetenu(): string | undefined {
  for (const f of FORMATS) {
    try {
      if (MediaRecorder.isTypeSupported(f)) return f;
    } catch {
      // Safari ancien : pas de `isTypeSupported`. On laissera le defaut.
    }
  }
  return undefined;
}

/** Au-dela, ce n'est plus une phrase : on borne le cout et la memoire. */
const DUREE_MAX_MS = 30_000;

export class Enregistreur {
  private flux: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private morceaux: Blob[] = [];
  private butoir: number | null = null;

  /**
   * Demande le micro, et **garde le flux ouvert** pour toute la conversation.
   *
   * C'est le meme principe que le moteur tenu au chaud : obtenir le flux coute
   * une permission puis l'ouverture du peripherique, plusieurs centaines de
   * millisecondes sur iPhone. Le payer a chaque appui ferait perdre les
   * premiers mots — precisement ce qu'on cherche a eviter.
   */
  async preparer(): Promise<void> {
    if (this.flux) return;
    this.flux = await navigator.mediaDevices.getUserMedia({
      audio: {
        // L'annulation d'echo evite que le micro reprenne la voix de synthese,
        // et la reduction de bruit aide dans une cuisine le matin.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  }

  /** Le bouton vient d'etre presse. */
  demarrer(): void {
    if (!this.flux || this.recorder) return;
    this.morceaux = [];
    const mimeType = formatRetenu();
    try {
      this.recorder = new MediaRecorder(this.flux, mimeType ? { mimeType } : undefined);
    } catch {
      this.recorder = null;
      return;
    }
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.morceaux.push(e.data);
    };
    this.recorder.start();
    // Un bouton reste parfois enfonce par accident, dans une poche. Sans
    // butoir, on enverrait des minutes de son a transcrire.
    this.butoir = window.setTimeout(() => void this.arreter(), DUREE_MAX_MS);
  }

  /**
   * Le bouton vient d'etre relache : rend ce qui a ete capte.
   *
   * Attendre `onstop` n'est pas une precaution : le dernier morceau de son
   * n'arrive qu'apres, et sans lui la phrase perdrait sa fin — le defaut qu'on
   * vient justement de corriger ailleurs.
   */
  arreter(): Promise<Blob | null> {
    const r = this.recorder;
    this.recorder = null;
    if (this.butoir !== null) {
      window.clearTimeout(this.butoir);
      this.butoir = null;
    }
    if (!r || r.state === 'inactive') return Promise.resolve(null);

    return new Promise((resoudre) => {
      const fini = () => {
        const morceaux = this.morceaux;
        this.morceaux = [];
        if (morceaux.length === 0) {
          resoudre(null);
          return;
        }
        const type = (r.mimeType || morceaux[0]?.type || 'audio/webm').split(';')[0] ?? 'audio/webm';
        resoudre(new Blob(morceaux, { type }));
      };
      r.onstop = fini;
      // Filet : sur certaines versions, `onstop` n'arrive pas si le flux a ete
      // coupe entre-temps. Une promesse en suspens bloquerait le tour.
      window.setTimeout(fini, 1200);
      try {
        r.stop();
      } catch {
        fini();
      }
    });
  }

  /** Rend le micro au systeme : l'indicateur d'enregistrement doit s'eteindre. */
  liberer(): void {
    void this.arreter();
    for (const piste of this.flux?.getTracks() ?? []) {
      try {
        piste.stop();
      } catch {
        // Deja arretee.
      }
    }
    this.flux = null;
  }
}

/**
 * Envoie le son au serveur et rend le texte.
 *
 * Le type MIME part dans l'en-tete : c'est lui qui dit au modele comment
 * decoder, et il differe selon le navigateur.
 */
export async function transcrire(audio: Blob): Promise<string> {
  const reponse = await fetch('/api/transcrire', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': audio.type || 'audio/webm' },
    body: audio,
  });
  if (!reponse.ok) throw new Error('transcription_' + String(reponse.status));
  const corps = (await reponse.json()) as { texte?: unknown };
  return typeof corps?.texte === 'string' ? corps.texte.trim() : '';
}
